import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchOrderInvoice, productPath, type OrderInvoiceResponse } from "../lib/catalog";
import { formatCurrency, toSentenceCase } from "../lib/utils";

export function InvoicePage() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [data, setData] = useState<OrderInvoiceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadInvoice() {
      if (!orderId) {
        setError("Missing order reference.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const query = new URLSearchParams();
        query.set("id", orderId);
        const result = await fetchOrderInvoice(query);
        if (!ignore) {
          setData(result);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load this invoice.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadInvoice();
    return () => {
      ignore = true;
    };
  }, [orderId]);

  const qrValue =
    data && orderId
      ? encodeURIComponent(
          `Novatech Invoice #${data.order.id}\nReference: ${data.order.trackingNumber || "N/A"}\nOrder: ${orderId}\nStatus: ${data.order.status}`
        )
      : null;
  const qrImageUrl = qrValue ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${qrValue}` : null;

  return (
    <section className="invoice-page mx-auto w-full max-w-5xl px-3 py-12 sm:px-4 lg:px-5">
      {loading ? (
        <div className="glass-panel rounded-[32px] p-8 text-sm text-nv-slate">Loading invoice...</div>
      ) : error || !data ? (
        <div className="glass-panel rounded-[32px] p-8">
          <h1 className="text-3xl font-semibold tracking-tight">Invoice unavailable</h1>
          <p className="mt-3 text-sm text-nv-slate">{error || "This invoice could not be found."}</p>
          <Link className="brand-button mt-6 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/shop">
            Back to shop
          </Link>
        </div>
      ) : (
        <div className="invoice-shell space-y-6">
          <div className="invoice-actions flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow">Invoice</p>
              <h1 className="mt-2 text-4xl font-bold tracking-tight">Order #{data.order.id}</h1>
            </div>
            <div className="flex gap-3">
              <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold" onClick={() => window.print()} type="button">
                Download / Print
              </button>
              <span className="rounded-full border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                Unpaid
              </span>
            </div>
          </div>

          <div className="invoice-actions rounded-[28px] border border-black/10 bg-black/5 p-5 text-sm leading-7 text-nv-slate">
            This invoice is currently marked unpaid. You can download or print it and use it as a quote while our
            team reaches out through official Novatech channels to guide delivery and payment.
          </div>

          <div className="glass-panel rounded-[32px] p-8 print:rounded-none">
            <div className="flex flex-wrap items-start justify-between gap-8 border-b border-black/10 pb-8">
              <div>
                <img alt="Novatech Logo" className="h-14 w-14 object-contain" src="/logo/logo.jpg" />
                <h2 className="mt-4 text-2xl font-semibold">Novatech</h2>
                <p className="mt-2 text-sm leading-7 text-nv-slate">
                  Royal Palms Mall, Mfangano Street
                  <br />
                  +254795952128
                  <br />
                  novatech.co.ke
                </p>
              </div>
              <div className="grid gap-3 text-sm text-nv-slate sm:text-right">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Status</p>
                  <p className="mt-1 inline-flex rounded-full border border-red-200 bg-red-50 px-3 py-1 text-sm font-semibold text-red-700">
                    Unpaid
                  </p>
                </div>
                {data.order.addedOn ? (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Issued</p>
                    <p className="mt-1 text-base text-nv-ink">{new Date(data.order.addedOn).toLocaleString()}</p>
                  </div>
                ) : null}
                {data.order.trackingNumber ? (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Reference</p>
                    <p className="mt-1 text-base text-nv-ink">{data.order.trackingNumber}</p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-6 border-b border-black/10 py-8 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nv-slate">Bill to</p>
                <div className="mt-3 space-y-2 text-sm text-nv-slate">
                  <p className="font-semibold text-nv-ink">{data.order.deliveryName}</p>
                  {data.order.email ? <p>{data.order.email}</p> : null}
                  <p>{data.order.deliveryPhone}</p>
                  <p className="whitespace-pre-line">{data.order.deliveryAddress}</p>
                  {data.order.deliveryNote ? <p>Note: {data.order.deliveryNote}</p> : null}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto py-8">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="w-[52%] py-3 pr-4">Item</th>
                    <th className="w-[12%] py-3">Qty</th>
                    <th className="w-[16%] py-3">Unit price</th>
                    <th className="w-[20%] py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr className="border-b border-black/5 align-top" key={item.id}>
                      <td className="py-4 pr-6">
                        {item.productId && item.slug ? (
                          <Link className="text-sm font-semibold hover:text-nv-electric" to={productPath(item.slug, item.productId)}>
                            {toSentenceCase(item.name)}
                          </Link>
                        ) : (
                          <span className="text-sm font-semibold">{toSentenceCase(item.name)}</span>
                        )}
                      </td>
                      <td className="py-4 text-sm">{item.quantity}</td>
                      <td className="py-4 text-sm">{formatCurrency(item.unitPrice)}</td>
                      <td className="py-4 text-right text-sm font-semibold">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ml-auto grid max-w-sm gap-3 border-t border-black/10 pt-6 text-sm">
              <div className="flex items-center justify-between text-nv-slate">
                <span>Subtotal</span>
                <span>{formatCurrency(data.order.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-nv-slate">
                <span>Discount</span>
                <span>{formatCurrency(data.order.discount)}</span>
              </div>
              <div className="flex items-center justify-between text-nv-slate">
                <span>Paid</span>
                <span>{formatCurrency(data.order.amountPaid)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-black/10 pt-3 text-base font-semibold">
                <span>Balance due</span>
                <span>{formatCurrency(data.order.balance)}</span>
              </div>
            </div>

            {qrImageUrl ? (
              <div className="mt-8 flex justify-center border-t border-black/10 pt-6">
                <div className="rounded-[28px] border border-black/10 bg-black/5 p-4 text-center">
                  <img alt={`QR code for invoice ${data.order.id}`} className="mx-auto h-32 w-32 object-contain" src={qrImageUrl} />
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-nv-slate">Scan invoice</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
